/** Shared shell layout tokens — keep in sync with index.css custom properties. */

/** Row 1 only (app bar + notch): 2.75rem + safe-area. */
export const SHELL_HEADER_COMPACT_PX = 44;

/** Single-row chrome with inline search: same height as compact on mobile. */
export const SHELL_HEADER_FULL_PX = 44;

/** @deprecated Use SHELL_HEADER_FULL_PX — kept for callers that expect one number. */
export const SHELL_HEADER_PX = SHELL_HEADER_FULL_PX;

/** CSS var consumed by sticky offsets site-wide. */
export const SHELL_HEADER_VAR = 'var(--shell-header)';

/** Mobile bottom tab island height (approx; taller hit targets). */
export const MOBILE_NAV_PX = 72;

/** CSS class for content padding above the mobile tab island. */
export const MOBILE_CONTENT_PB =
  'pb-[calc(6.5rem+env(safe-area-inset-bottom,0px))] md:pb-16';

/** CSS class for fixed/sticky footers that sit above the mobile tab island. */
export const MOBILE_STICKY_FOOTER =
  'fixed inset-x-0 bottom-[calc(6.5rem+env(safe-area-inset-bottom,0px))] z-20 md:static md:inset-auto';

/** Main tab routes — logo-centered topbar (no back affordance). */
export const MAIN_TAB_PATHS = ['/', '/library', '/profile', '/report'] as const;

/**
 * Mobile bottom-bar order (left → right, skipping the center +).
 * Used for carousel slide direction between primary tabs.
 */
export const CAROUSEL_TAB_ORDER = ['/', '/library', '/report', '/profile'] as const;

/**
 * Routes that expose the global search pill (library keeps its own page search).
 * Surgery detail also shows it so you can jump to any matching case from a ficha.
 */
export const GLOBAL_SEARCH_TAB_PATHS = ['/', '/report'] as const;

export type ShellHeaderMode = 'compact' | 'full';

/** -1 left, 0 fade/vertical, +1 right — for tab carousel enter/exit. */
export type CarouselDirection = -1 | 0 | 1;

export function isMainTab(pathname: string): boolean {
  return (MAIN_TAB_PATHS as readonly string[]).includes(pathname);
}

/** Map a pathname onto the mobile tab strip index, or -1 if not a strip tab. */
export function carouselTabIndex(pathname: string): number {
  const base = pathname.split('?')[0]?.split('#')[0] ?? pathname;
  if (base.startsWith('/surgery/')) {
    return CAROUSEL_TAB_ORDER.indexOf('/library');
  }
  return (CAROUSEL_TAB_ORDER as readonly string[]).indexOf(base);
}

/**
 * Direction for a route change: positive = next tab (enter from right),
 * negative = previous tab (enter from left), 0 = not a lateral tab move.
 */
export function carouselDirection(fromPath: string, toPath: string): CarouselDirection {
  const from = carouselTabIndex(fromPath);
  const to = carouselTabIndex(toPath);
  if (from < 0 || to < 0 || from === to) return 0;
  return to > from ? 1 : -1;
}

/** Whether this navigation should slide up like a sheet (/new, /edit). */
export function isSheetRoute(pathname: string): boolean {
  const base = pathname.split('?')[0]?.split('#')[0] ?? pathname;
  return base === '/new' || base.startsWith('/edit/');
}

/** Whether the two-row topbar shows the global search pill. */
export function shellHeaderMode(pathname: string): ShellHeaderMode {
  const base = pathname.split('?')[0]?.split('#')[0] ?? pathname;
  // From a case ficha, keep the always-on search to find other matching records.
  if (base.startsWith('/surgery/')) return 'full';
  if (base === '/library' || base === '/profile') return 'compact';
  if (!isMainTab(base)) return 'compact';
  return 'full';
}

/** Apply shell mode to `<html data-shell>` so `--shell-header` tracks the live topbar. */
export function applyShellHeaderMode(mode: ShellHeaderMode): void {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.shell = mode;
}
